/*
 * PDP add-on model (Keelan 09-27). On the configurator desks, checking any PDP add-on
 * toggle swaps the MAIN gallery area to a model of the selected size + colour + the checked
 * add-ons. Interactive like the configurator modal (drag to rotate, pinch/scroll to zoom,
 * same camera limits); no auto-rotate, no AR, no extra UI, never in the thumbnails or the
 * lightbox. While it shows, no thumbnail reads as selected (Keelan 09-27).
 *
 * It is a LAYER over [data-gallery-main], not a gallery slide. The photo underneath is
 * never touched. Keelan 09-27: once open, the 3D view STAYS - unchecking every add-on
 * shows the plain desk; checkboxes only change what is on the desk. Dragging rotates the
 * model, so the ONLY ways back to photos are a thumbnail or a gallery arrow; the next
 * add-on click opens the 3D view again. The configurator modal is a separate thing and is
 * not touched: this file reads the same combo table and config block, and writes nothing
 * the modal reads.
 *
 * Lazy: model-viewer and the combo table load on the FIRST add-on toggle only. Nothing
 * extra is fetched on page load.
 */
(function () {
  'use strict';

  var MV_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.3.1/model-viewer.min.js';

  var main = document.querySelector('[data-gallery-main]');
  var cfgEl = document.querySelector('[data-configurator-config]');
  if (!main || !cfgEl) return;

  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var variantInput = document.querySelector('[data-variant-id]');
  var combos = null;
  var product = null;
  var combosReq = null;
  var layer = null;
  var viewers = [];
  var front = 0;
  var shownKey = null;   /* combo on the front viewer */
  var pendingKey = null; /* combo loading into the back viewer */
  var inView = false;    /* the 3D view is the main image right now */
  var lastVariant = variantInput ? variantInput.value : null;

  function loadLibrary() {
    if (window.customElements && window.customElements.get('model-viewer')) return;
    if (document.querySelector('script[src="' + MV_SRC + '"]')) return; /* the modal may have asked already */
    var s = document.createElement('script');
    s.type = 'module';
    s.src = MV_SRC;
    document.head.appendChild(s);
  }

  function loadCombos() {
    if (!combosReq) {
      combosReq = fetch(cfg.combosUrl)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          combos = d; product = (d.products || {})[cfg.handle] || null;
          /* The product's framing box: default zoom AND the zoom-out limit. */
          if (product && product.frame) BIG = product.frame;
          /* The dining scene opens a touch higher, so the top reads past the chairs. */
          if (product && product.scene && product.scene.orbit) {
            THETA0 = parseFloat(product.scene.orbit.split(' ')[0]) || THETA0;
            PHI0 = parseFloat(product.scene.orbit.split(' ')[1]) || PHI0;
          }
        });
    }
    return combosReq;
  }

  function size() {
    var id = variantInput && variantInput.value;
    return (id && cfg.variantSizes[id]) || cfg.initialSize;
  }

  function checkedTokens() {
    var out = [];
    document.querySelectorAll('[data-addon-item]').forEach(function (li) {
      var cb = li.querySelector('[data-addon-checkbox]');
      if (!cb || !cb.checked) return;
      /* The dining scene keys its cards by "handle:qty" (Add 2/4/6/8 Chairs are one
         product); a card hidden at this size (benches on the 48in) shows nothing. */
      var token = product && (product.scene
        ? (!li.hidden && (product.accessories[li.getAttribute('data-addon-key')] || product.accessories[li.getAttribute('data-addon-slug')]))
        : product.accessories[li.getAttribute('data-addon-slug')]);
      if (token && out.indexOf(token) === -1) out.push(token);
    });
    return out;
  }

  /* The chair colour picked in the add-on colour row (dining scene only). */
  function sceneColor() {
    var pick = document.querySelector('[data-addon-color].is-selected');
    var c = pick ? pick.textContent.trim().toLowerCase() : '';
    return product.scene.colors[c] ? c : Object.keys(product.scene.colors)[0];
  }

  /* Same key rule as the configurator: family-size-finish then accessoryOrder (then the
     chair colour, for a scene with chairs in it). */
  function comboKey(acc) {
    var k = product.family + '-' + size() + '-' + product.finish;
    combos.accessoryOrder.forEach(function (t) { if (acc.indexOf(t) !== -1) k += '-' + t; });
    var ct = product.scene && product.scene.colorTokens;
    if (ct && acc.some(function (t) { return ct.indexOf(t) !== -1; })) k += '-' + sceneColor();
    return k;
  }

  function build() {
    var style = document.createElement('style');
    style.textContent =
      '.ks-addon-model{position:absolute;inset:0;z-index:4;opacity:0;pointer-events:none;' +
      'transition:opacity .35s ease;background:var(--ks-addon-model-bg,#fff)}' +
      '.ks-addon-model.is-on{opacity:1;pointer-events:auto;cursor:default}' +
      /* Hidden = out of hit-testing entirely once the fade ends: model-viewer's shadow
         poster sets its own pointer-events, so pointer-events:none alone leaks through. */
      '.ks-addon-model:not(.is-on){visibility:hidden;transition:opacity .35s ease,visibility 0s linear .35s}' +
      /* z-index is load-bearing (same discipline as the configurator modal). The two
         viewers are stacked siblings, so without it DOM order decides and viewer #2 sits
         on top whether or not it is the visible one - after every second swap the
         shopper's drag went to the invisible idle viewer. The live viewer is always on
         top and the only one taking input; the idle one sits under it, and model-viewer's
         shadow poster (pointer-events:initial) cannot escape its host's stacking context. */
      '.ks-addon-model__viewer{position:absolute;inset:0;width:100%;height:100%;opacity:0;z-index:0;' +
      'pointer-events:none;transition:opacity .45s ease;--poster-color:transparent;background:transparent}' +
      '.ks-addon-model__viewer.is-front{opacity:1;z-index:1}' +
      /* Input only while the 3D view is showing. A child's pointer-events:auto overrides
         the hidden layer's none, so without the .is-on gate the invisible viewer kept the
         grab cursor and swallowed clicks on the photos after exiting (Keelan 09-27). */
      '.ks-addon-model.is-on .ks-addon-model__viewer.is-front{pointer-events:auto;cursor:grab}' +
      '.ks-addon-model.is-on .ks-addon-model__viewer.is-front:active{cursor:grabbing}' +
      '.ks-addon-model__viewer::part(default-progress-bar){display:none}' +
      '.ks-addon-model__extra{position:absolute;z-index:3;top:12px;left:12px;display:inline-flex;align-items:center;gap:6px;' +
      'padding:5px 11px 5px 8px;border:1px solid rgba(0,0,0,.08);border-radius:999px;background:rgba(255,255,255,.94);' +
      'color:#1a1a1a;font-size:.8rem;line-height:1.2;pointer-events:none}' +
      '.ks-addon-model__extra[hidden]{display:none}' +
      '.ks-addon-model__extra svg{width:20px;height:20px;flex:none}' +
      '.ks-addon-model__extra path{stroke:rgba(0,0,0,.55);stroke-width:1;stroke-linejoin:round}' +
      /* Phone dock (Keelan 10-20): see the dock block at the bottom of this file. Every
         rule hangs off .is-docked, which only a phone ever gets, so desktop is untouched. */
      '.ks-addon-model.is-docked{position:fixed;inset:auto;left:10px;right:10px;top:var(--ks-dock-top,10px);' +
      'height:var(--ks-dock-h,40vh);z-index:990;border-radius:14px;background:#fff;' +
      'box-shadow:0 10px 30px rgba(0,0,0,.18),0 0 0 1px rgba(0,0,0,.06);overflow:hidden}' +
      /* Quick fade-in each time the card shows (Keelan 10-20); hiding stays as it was.
         Keyed to .is-on so a first tap, whose model is still loading, fades in when the
         card actually becomes visible rather than while it is still invisible. */
      '.ks-addon-model.is-docked.is-on:not(.is-tucked){animation:ks-dock-in .18s ease-out}' +
      /* Follows the header as it moves, but only once in place: on the first frame the
         layer's top is still its spot in the gallery, and it must not slide from there. */
      '.ks-addon-model.is-docked.is-settled{transition:top .25s ease}' +
      '.ks-addon-model.is-docked.is-tucked{opacity:0;pointer-events:none;visibility:hidden;' +
      'transition:opacity .2s ease,visibility 0s linear .2s}' +
      '@keyframes ks-dock-in{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:none}}' +
      /* model-viewer redraws its canvas one frame after its box resizes, so that frame
         is held back and the model fades in once the canvas matches the box. */
      '.ks-addon-model.is-resizing .ks-addon-model__viewer.is-front{opacity:0;transition:none}' +
      '.ks-addon-model__undock{display:none}' +
      '.ks-addon-model.is-docked .ks-addon-model__undock{display:flex;position:absolute;z-index:3;top:8px;right:8px;' +
      'width:32px;height:32px;align-items:center;justify-content:center;padding:0;border:1px solid rgba(0,0,0,.08);' +
      'border-radius:50%;background:rgba(255,255,255,.94);color:#1a1a1a;cursor:pointer}' +
      '.ks-addon-model__undock svg{width:16px;height:16px}' +
      /* Back to the photos (Keelan 10-27): same x as the floating card's, shown only on the
         3D view in the gallery. 32px to look at, 44px to hit. */
      '.ks-addon-model__close{display:none}' +
      '.ks-addon-model.is-on:not(.is-docked) .ks-addon-model__close{display:flex;position:absolute;z-index:3;top:8px;right:8px;' +
      'width:32px;height:32px;align-items:center;justify-content:center;padding:0;border:1px solid rgba(0,0,0,.08);' +
      'border-radius:50%;background:rgba(255,255,255,.94);color:#1a1a1a;cursor:pointer}' +
      '.ks-addon-model__close:after{content:"";position:absolute;inset:-6px}' +
      '.ks-addon-model__close svg{width:16px;height:16px}' +
      /* Add-on tap feedback (Keelan 10-27): the moment a box is checked, a soft shimmer
         and a small spinner sit over the 3D view until the new model swaps in. */
      '.ks-addon-model__loading{position:absolute;inset:0;z-index:2;pointer-events:none;opacity:0;' +
      'transition:opacity .2s ease;overflow:hidden}' +
      '.ks-addon-model.is-loading .ks-addon-model__loading{opacity:1;transition:none}' +
      /* Before the first model shows the card is plain white, and a white sweep on white
         is invisible (Keelan 10-28 saw only the spinner): a light grey base gives it
         something to sweep across. Once a model is up, the sweep runs over the model. */
      '.ks-addon-model:not(.has-model) .ks-addon-model__loading{background:#ececec}' +
      '.ks-addon-model__loading:before{content:"";position:absolute;inset:0;transform:translateX(-100%);' +
      'background:linear-gradient(100deg,transparent 20%,rgba(255,255,255,.7) 50%,transparent 80%);' +
      'animation:ks-model-shimmer 1.1s ease-in-out infinite}' +
      '.ks-addon-model__loading:after{content:"";position:absolute;left:50%;top:50%;width:28px;height:28px;' +
      'margin:-14px 0 0 -14px;border-radius:50%;border:2.5px solid rgba(0,0,0,.12);border-top-color:rgba(0,0,0,.55);' +
      'animation:ks-model-spin .7s linear infinite}' +
      '@keyframes ks-model-shimmer{to{transform:translateX(100%)}}' +
      '@keyframes ks-model-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);

    layer = document.createElement('div');
    layer.className = 'ks-addon-model';
    layer.setAttribute('aria-hidden', 'true');
    var bg = window.getComputedStyle(main).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') layer.style.setProperty('--ks-addon-model-bg', bg);

    for (var i = 0; i < 2; i++) {
      var v = document.createElement('model-viewer');
      v.className = 'ks-addon-model__viewer';
      /* Same camera behaviour as the configurator modal's viewers. */
      v.setAttribute('camera-controls', '');
      v.setAttribute('disable-pan', '');   /* rotate + zoom only; the desk stays the centre (Keelan 09-27) */
      v.setAttribute('interaction-prompt', 'none');
      v.setAttribute('min-field-of-view', '18deg');
      v.setAttribute('max-field-of-view', '45deg');
      v.setAttribute('shadow-intensity', '1');
      v.setAttribute('shadow-softness', '0.85');
      v.setAttribute('exposure', '0.9');
      v.setAttribute('environment-image', 'neutral');
      v.setAttribute('loading', 'eager');
      v.setAttribute('reveal', 'auto');
      /* No built-in loading bar: the cross-fade already hides loading (Keelan 09-27). An
         empty slot replaces model-viewer's default bar for these viewers only. */
      var noBar = document.createElement('div');
      noBar.setAttribute('slot', 'progress-bar');
      v.appendChild(noBar);
      v.addEventListener('load', onLoad);
      v.addEventListener('error', function () { pendingKey = null; settleNoModel(); });
      viewers.push(v);
      layer.appendChild(v);
    }
    /* The layer is not the photo: its clicks and swipes must not open the lightbox or
       page the gallery underneath. */
    ['click', 'pointerdown'].forEach(function (ev) {
      layer.addEventListener(ev, function (e) { e.stopPropagation(); });
    });
    ['pointerdown', 'touchstart', 'wheel'].forEach(function (ev) {
      layer.addEventListener(ev, function () { lastPointerAt = lastPointerAt + 1; }, { passive: true, capture: true });
    });
    /* A drag on the model rotates it; it must not also page the photo gallery underneath. */
    ['touchstart', 'touchend'].forEach(function (ev) {
      layer.addEventListener(ev, function (e) { e.stopPropagation(); }, { passive: true });
    });
    loadingEl = document.createElement('div');
    loadingEl.className = 'ks-addon-model__loading';
    layer.appendChild(loadingEl);
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'ks-addon-model__close';
    closeBtn.setAttribute('aria-label', 'Back to photos');
    closeBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
    closeBtn.addEventListener('click', function (e) { e.stopPropagation(); exitView(); });
    layer.appendChild(closeBtn);
    main.appendChild(layer);
    if (dock) dock.attach();
    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        var f = viewers[front];
        if (held && shownKey && f.loaded && lastPointerAt === heldAt) restoreCamera(f, held);
      }).observe(layer);
    }
    /* A real box resize (rotation, desktop window) re-frames; a phone's URL bar showing
       or hiding fires resize too without changing the box, and must not touch the zoom. */
    var rt = null, lastBox = layer.clientWidth + 'x' + layer.clientHeight;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        var box = layer.clientWidth + 'x' + layer.clientHeight;
        if (box === lastBox) return;
        lastBox = box;
        var f = viewers[front];
        if (f && f.loaded) applyCamera(f, defaultCamera(f));
      }, 150);
    });
  }

  function onLoad(e) {
    var v = e.target;
    if (!pendingKey || !combos.combos[pendingKey]) return;
    if (v !== viewers[1 - front] || v.getAttribute('src') !== combos.combos[pendingKey].glb) return;
    swap();
  }

  /* ---- camera: ONE fixed framing per product, held across every swap -------------
     The same handling as the configurator modal (product-configurator.js, which explains
     the why at length), copied because that file is a closed IIFE. In short: model-viewer
     renders the field-of-view attribute through each model's own shape on a narrow box,
     so the same attribute zoomed differently on the 46 and the 58 and a read-back
     re-apply zoomed out a step per add-on click on phones (Keelan 10-08). The shopper's
     view is now held as the real distance + angle + on-screen scale, every model gets
     the attribute that renders exactly that on it, and the target never moves. The
     default distance is one fit of the product's frame box (the union of its combos, so
     its largest: measured with model-viewer getDimensions() / getBoundingBoxCenter() on
     the real GLBs, re-measure if they are re-exported) at every turntable angle; zooming
     in is allowed, zooming out past it is not. */
  var lastPointerAt = 0;
  var FOV = 30;          /* degrees: vertical on a landscape box, horizontal on a portrait one */
  var MIN_FOV = 18;
  /* Opening angle from the config block (defaultOrbit, shared with the modal). */
  var ORBIT0 = (cfg.defaultOrbit || '0deg 75deg').split(' ');
  var THETA0 = parseFloat(ORBIT0[0]) || 0;
  var PHI0 = parseFloat(ORBIT0[1]) || 75;
  var BIG = null;
  var fitCache = {};

  /* The exact (fractional) box model-viewer sizes itself by; clientWidth rounds, and that
     rounding compounded through attrFov into a creep of ~0.005deg per swap. */
  function aspectOf(v) {
    var b = v ? v.getBoundingClientRect() : null;
    if (!b || !b.width || !b.height) b = layer.getBoundingClientRect();
    return b.width && b.height ? b.width / b.height : null;
  }

  function baseVfov(aspect) {
    if (aspect >= 1) return FOV;
    return Math.atan(Math.tan(FOV * Math.PI / 360) / aspect) * 360 / Math.PI;
  }

  /* model-viewer renders an attribute F as 2*atan(tan(F/2) * max(1, idealAspect/aspect)),
     idealAspect being the loaded model's. Invert it so every model renders `vfov`. */
  function attrFov(v, vfov) {
    var a = aspectOf(v), ideal = v.getIdealAspect ? v.getIdealAspect() : 0;
    var k = a && ideal ? Math.max(1, ideal / a) : 1;
    return Math.atan(Math.tan(vfov * Math.PI / 360) / k) * 360 / Math.PI;
  }

  /* Same target + fit points as product-configurator.js: a frame may set its own target
     height (ty) and a footprint (pts) in place of the box corners. */
  function frameTarget(b) {
    return [b.cx, b.ty != null ? b.ty : b.cy, b.cz];
  }

  function framePoints(b) {
    var t = frameTarget(b), out = [];
    if (b.pts) {
      b.pts.forEach(function (p) { [0, b.y].forEach(function (y) { out.push([p[0] - t[0], y - t[1], p[1] - t[2]]); }); });
      return out;
    }
    [-1, 1].forEach(function (sx) { [-1, 1].forEach(function (sy) { [-1, 1].forEach(function (sz) {
      out.push([b.cx + sx * b.x / 2 - t[0], b.cy + sy * b.y / 2 - t[1], b.cz + sz * b.z / 2 - t[2]]);
    }); }); });
    return out;
  }

  function frameRadius(aspect) {
    if (!BIG || !aspect) return null;
    var ck = aspect.toFixed(4);
    if (fitCache[ck]) return fitCache[ck];
    var tv = Math.tan(baseVfov(aspect) * Math.PI / 360), th = tv * aspect;
    /* Fitted at EVERY turntable angle, not just the opening one: a long piece's widest
       silhouette is near front-on, so a fit at 30deg clipped the ends as it rotated. */
    var phis = [PHI0];
    var pts = framePoints(BIG), r = 0;
    phis.forEach(function (pdeg) {
      var phi = pdeg * Math.PI / 180;
      for (var a = 0; a < 360; a += 5) {
        var th0 = a * Math.PI / 180, st = Math.sin(th0), ct = Math.cos(th0);
        var dir = [st * Math.sin(phi), Math.cos(phi), ct * Math.sin(phi)];   /* target -> camera */
        var up = [-st * Math.cos(phi), Math.sin(phi), -ct * Math.cos(phi)];
        var right = [ct, 0, -st];
        pts.forEach(function (p) {
          var along = p[0] * dir[0] + p[1] * dir[1] + p[2] * dir[2];
          var py = p[0] * up[0] + p[1] * up[1] + p[2] * up[2];
          var px = p[0] * right[0] + p[1] * right[1] + p[2] * right[2];
          r = Math.max(r, along + Math.abs(py) / tv, along + Math.abs(px) / th);
        });
      }
    });
    return (fitCache[ck] = +(r * 1.08).toFixed(4));
  }

  /* Closest zoom-in distance: a product constant (model-viewer's default is 2.2x the
     LOADED model's bounding sphere, so it changed per combo and clamped on a swap). */
  function minRadius() {
    return +(1.1 * Math.sqrt(BIG.x * BIG.x + BIG.y * BIG.y + BIG.z * BIG.z)).toFixed(4);
  }

  /* The zoom is held as a FOCAL LENGTH in screen pixels, not an angle, so a box that
     changes height cannot change what the shopper sees; the fit distance and both zoom
     limits are locked when the default view is set. */
  var lock = null;

  function heightOf(v) {
    var b = v ? v.getBoundingClientRect() : null;
    if (!b || !b.height) b = layer.getBoundingClientRect();
    return b.height || null;
  }

  function vfovFor(v, focal) { return Math.atan(heightOf(v) / 2 / focal) * 360 / Math.PI; }
  function focalFor(v, vfov) { return heightOf(v) / 2 / Math.tan(vfov * Math.PI / 360); }

  function applyLimits(v) {
    if (!lock) return null;
    var maxPhi = product && product.scene ? product.scene.maxPhi + 'deg' : 'auto';
    v.setAttribute('max-camera-orbit', 'auto ' + maxPhi + ' ' + lock.radius + 'm');
    v.setAttribute('min-camera-orbit', 'auto auto ' + Math.min(minRadius(), lock.radius) + 'm');
    v.setAttribute('max-field-of-view', attrFov(v, vfovFor(v, lock.focal)) + 'deg');
    v.setAttribute('min-field-of-view', vfovFor(v, lock.focalMax) + 'deg');
    return lock.radius;
  }

  /* The last view put on a viewer. A read-back that matches it to within float noise IS
     it, so an untouched swap carries the exact same numbers instead of re-rounding them. */
  var held = null, heldAt = -1;
  function sameView(a, b) {
    return a && b && Math.abs(a.radius - b.radius) < 1e-4 && Math.abs(a.focal / b.focal - 1) < 1e-4 &&
      Math.abs(a.theta - b.theta) < 1e-3 && Math.abs(a.phi - b.phi) < 1e-3;
  }

  /* Untouched since the last apply (no drag, pinch or wheel): the held view IS the view;
     model-viewer's own resize rescale is not exact, so it is never read back. */
  function captureCamera(v) {
    var c = readCamera(v);
    return c && held && (sameView(c, held) || lastPointerAt === heldAt) ? held : c;
  }

  function readCamera(v) {
    try {
      var orbit = v.getCameraOrbit(), fov = v.getFieldOfView();
      if (!orbit || !fov || !heightOf(v)) return null;
      return { theta: orbit.theta * 180 / Math.PI, phi: orbit.phi * 180 / Math.PI, radius: orbit.radius, focal: focalFor(v, fov) };
    } catch (err) { return null; }
  }

  function defaultCamera(v) {
    var a = aspectOf(v), r = frameRadius(a);
    if (!r || !heightOf(v)) return null;
    var v0 = baseVfov(a), f0 = focalFor(v, v0);
    lock = { radius: r, focal: f0, focalMax: f0 * Math.tan(v0 * Math.PI / 360) / Math.tan(MIN_FOV * Math.PI / 360) };
    return { theta: THETA0, phi: PHI0, radius: r, focal: f0 };
  }

  function applyCamera(v, cam) {
    if (!BIG || !cam || !lock) return;
    applyLimits(v);
    if (held !== cam) heldAt = lastPointerAt;
    held = cam;
    v.setAttribute('camera-orbit', cam.theta.toFixed(4) + 'deg ' + cam.phi.toFixed(4) + 'deg ' + cam.radius + 'm');
    var t = frameTarget(BIG);
    v.setAttribute('camera-target', t[0] + 'm ' + t[1] + 'm ' + t[2] + 'm');
    v.setAttribute('field-of-view', attrFov(v, vfovFor(v, cam.focal)) + 'deg');
    if (v.jumpCameraToGoal) v.jumpCameraToGoal();
  }

  /* model-viewer re-frames after load when the bounding box changes (a size swap), so
     re-assert until it settles, and stop the moment the shopper touches the model. */
  function restoreCamera(v, cam) {
    if (!cam) return;
    applyCamera(v, cam);
    /* Watch for 1.5s and put the view back whenever it has slipped: on a slow phone the
       re-frame can land well after any fixed timer would have fired. */
    var at = lastPointerAt, until = Date.now() + 1500;
    var watch = function () {
      if (lastPointerAt !== at || held !== cam) return;
      if (!sameView(readCamera(v), cam)) applyCamera(v, cam);
      if (Date.now() < until) requestAnimationFrame(watch);
    };
    requestAnimationFrame(watch);
  }

  /* Every swap (size, colour, add-on, scene) carries the shopper's camera exactly; the
     first model shown opens on the default view. */
  function nextCamera(from) {
    var c = shownKey && from && from.loaded ? captureCamera(from) : null;
    return c || 'default';
  }

  /* Shown at least a beat so a cached swap still reads as a registered tap. */
  var loadingEl = null, loadingAt = 0, loadingT = null;
  function startLoading() {
    if (!layer) return;
    clearTimeout(loadingT);
    loadingAt = Date.now();
    layer.classList.add('is-loading');
  }
  function stopLoading() {
    if (!layer) return;
    clearTimeout(loadingT);
    loadingT = setTimeout(function () { layer.classList.remove('is-loading'); },
      Math.max(0, 350 - (Date.now() - loadingAt)));
  }

  function swap() {
    var back = 1 - front;
    /* Read the camera at swap time off the visible viewer, so a drag made while the new
       model downloaded is kept (the bug fixed in the modal the same day). */
    var cam = nextCamera(viewers[front]);
    restoreCamera(viewers[back], cam === 'default' ? defaultCamera(viewers[back]) : cam);
    viewers[back].classList.add('is-front');
    viewers[front].classList.remove('is-front');
    front = back;
    shownKey = pendingKey;
    pendingKey = null;
    layer.classList.add('has-model');
    stopLoading();
    if (inView) showLayer();
  }

  var savedThumb = null;
  function clearThumbs() {
    var on = document.querySelector('[data-gallery-thumbnail].is-active');
    if (on) { savedThumb = on; on.classList.remove('is-active'); }
  }

  /* The 3D view covers the gallery video, which kept playing (and its music running)
     underneath (Keelan 10-22). Quick fade of the sound, then pause. iOS Safari ignores
     video.volume, so there the pause is immediate. The video's own thumbnail restarts it. */
  var fade = null;
  function stopFade(restore) {
    if (!fade) return;
    cancelAnimationFrame(fade.raf);
    if (restore) fade.v.volume = fade.vol;
    fade = null;
  }
  function quietVideo() {
    var v = main.querySelector('[data-gallery-video]');
    if (!v || v.paused || fade) return;
    var vol = v.volume;
    var probe = vol > 0.5 ? vol - 0.25 : vol + 0.25;
    v.volume = probe;
    var canFade = Math.abs(v.volume - probe) < 0.01;
    v.volume = vol;
    if (v.muted || !canFade || !vol) { v.pause(); return; }
    var t0 = performance.now(), MS = 400;
    fade = { v: v, vol: vol, raf: 0 };
    var step = function (now) {
      var k = Math.min(1, (now - t0) / MS);
      v.volume = vol * (1 - k);
      if (k < 1) { fade.raf = requestAnimationFrame(step); return; }
      v.pause();
      stopFade(true);
    };
    fade.raf = requestAnimationFrame(step);
  }
  function showLayer() {
    layer.classList.add('is-on');
    clearThumbs();
    quietVideo();
    if (dock) dock.emit();
  }

  function exitView() {
    inView = false;
    stopFade(true);
    pendingKey = null;
    if (layer) { layer.classList.remove('is-on'); layer.classList.remove('is-loading'); }
    if (dock) dock.release();
    /* The thumbnail/arrow click that brought us here sets its own selection right after;
       put the old one back only in case nothing does. */
    var keep = savedThumb;
    savedThumb = null;
    setTimeout(function () {
      if (keep && !document.querySelector('[data-gallery-thumbnail].is-active')) keep.classList.add('is-active');
    }, 0);
  }

  /* Chairs ordered past what the table seats are NOT modelled (Keelan 10-08 final): a
     badge pinned top-left over the 3D view, outside the scene, says how many more come
     with the order, its chair tinted to the picked colour. Same badge as the modal. */
  var extraEl = null;
  function syncExtra(key, acc) {
    var sc = product && product.scene;
    if (!sc) return;
    var ct = sc.colorTokens || [];
    var tinted = acc.some(function (t) { return ct.indexOf(t) !== -1; });
    var n = (sc.extra && sc.extra[tinted ? key.slice(0, key.lastIndexOf('-')) : key]) || 0;
    if (!extraEl) {
      extraEl = document.createElement('div');
      extraEl.className = 'ks-addon-model__extra';
      extraEl.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2.5a1.5 1.5 0 0 1 1.5-1.5h1a1.5 1.5 0 0 1 1.5 1.5v8h7.5a1.5 1.5 0 0 1 1.5 1.5v1.5a1 1 0 0 1-1 1h-.5v7.5h-2v-7.5h-7v7.5H6z"/></svg><span></span>';
      layer.appendChild(extraEl);
    }
    extraEl.hidden = !n;
    if (!n) return;
    var c = sceneColor();
    extraEl.querySelector('path').style.fill = (sc.tints && sc.tints[c]) || '#999';
    extraEl.querySelector('span').textContent = '+' + n + ' additional';
  }

  /* Nothing new to load: drop the spinner, and if no model ever showed (the white card
     only went up for the tap) put the photos back. */
  function settleNoModel() {
    if (!layer) return;
    stopLoading();
    if (!shownKey) exitView();
  }

  function update() {
    if (!combos || !product || !inView) { if (combos) settleNoModel(); return; }
    if (!layer) build();
    var acc = checkedTokens();
    var key = comboKey(acc);                /* none checked = the plain desk */
    var urls = combos.combos[key];
    if (!urls) { settleNoModel(); return; } /* no model for this combo: keep what is showing */
    syncExtra(key, acc);
    /* An overflow combo reuses the max-seated scene (Keelan 10-08 final): when that file
       is already on screen only the badge changes. */
    if (key === shownKey || (shownKey && combos.combos[shownKey] && combos.combos[shownKey].glb === urls.glb)) {
      shownKey = key; pendingKey = null; stopLoading(); showLayer(); return;
    }
    if (key === pendingKey) return;
    pendingKey = key;
    var back = viewers[1 - front];
    var pre = nextCamera(viewers[front]);
    applyCamera(back, pre === 'default' ? defaultCamera(back) : pre);
    if (back.getAttribute('src') === urls.glb && back.loaded) { swap(); return; }
    startLoading();
    back.setAttribute('src', urls.glb);
  }

  document.addEventListener('change', function (e) {
    if (!e.target.matches || !e.target.matches('[data-addon-checkbox]')) return;
    /* Last add-on unchecked: back to the photos (Keelan 10-28, replaces 09-27's "stays
       on the plain desk"). The next add-on tap opens the 3D view again. */
    if (!e.target.checked && !anyChecked()) { if (inView) exitView(); return; }
    inView = true;                          /* any add-on click opens (or keeps) the 3D view */
    /* Build now, not after the combo table lands, so the tap shows on the 3D view at once. */
    if (!layer) build();
    startLoading();
    showLayer();
    /* Unchecking never pops the floating card; it only updates a card already floating
       (Keelan 10-27). The gallery's 3D view updates either way. */
    if (dock && (e.target.checked || dock.isDocked())) dock.request(e.target);
    loadLibrary();
    loadCombos().then(update, settleNoModel);
  });

  function anyChecked() {
    return Array.prototype.some.call(document.querySelectorAll('[data-addon-item]'), function (li) {
      var cb = li.querySelector('[data-addon-checkbox]');
      return !li.hidden && cb && cb.checked;
    });
  }

  /* Size change: the variant input is set by script (no event), so re-check after any
     interaction with the product form. */
  function checkVariant() {
    if (!variantInput || variantInput.value === lastVariant) return;
    lastVariant = variantInput.value;
    if (combos && inView) update();
  }
  ['change', 'click'].forEach(function (ev) {
    document.addEventListener(ev, function () { setTimeout(checkVariant, 0); }, true);
  });

  /* Thumbnails and arrows are the way back to photos. */
  document.addEventListener('click', function (e) {
    if (!inView) return;
    if (e.target.closest && e.target.closest('[data-gallery-thumbnail],[data-gallery-prev],[data-gallery-next]')) exitView();
  }, true);
  /* ---- phone dock (Keelan 10-20) ---------------------------------------------------
     On a phone the gallery is at the top and the add-on cards sit a long scroll below
     it, so the 3D view an add-on tap opens lands somewhere the shopper has already
     scrolled past. On a phone ONLY, when the gallery is mostly off screen, the same
     3D layer lifts out of the gallery into a floating card pinned under the header,
     so the shopper sees the model change right where they tapped. Same viewers, same
     camera, nothing reloads; it is a class on the layer, not a second viewer.
       - scroll back up to the gallery: it settles back into the gallery by itself
       - scroll past the add-ons: it tucks away; scroll back to them and it returns
       - the x closes it for the rest of the page visit (the gallery keeps the 3D view)
       - unchecking an add-on never pops it; it only updates a card already showing
     Desktop never builds any of this: the media query gates the whole block. */
  var dock = null;
  var PHONE = window.matchMedia ? window.matchMedia('(max-width: 600px)') : null;
  if (PHONE && PHONE.matches && window.IntersectionObserver) dock = (function () {
    var docked = false, dismissed = false, armed = false;
    var galleryShown = true;
    var addonsBox = document.querySelector('[data-product-addons]');
    /* Whatever header is pinned at the top right now (the phone header is the sticky
       header section; the nav bar pins itself on wider screens). */
    var tops = document.querySelectorAll('.section-header, .header__nav-wrapper');

    function topEdge() {
      var b = 0;
      Array.prototype.forEach.call(tops, function (el) {
        var r = el.getBoundingClientRect();
        if (r.top <= 1 && r.bottom > b && r.height) b = r.bottom;
      });
      return Math.round(b) + 10;
    }

    /* The card keeps roughly the gallery's shape, capped so a good part of the add-on
       list stays visible under it. */
    function sizeIt() {
      var w = window.innerWidth - 20;
      var h = Math.round(Math.min(w * 0.72, window.innerHeight * 0.38));
      layer.style.setProperty('--ks-dock-top', topEdge() + 'px');
      layer.style.setProperty('--ks-dock-h', h + 'px');
    }

    /* The box changed size, so the held zoom no longer fits it: re-fit to the new box,
       keeping the angle the shopper turned it to. Synchronous, in the same task as the
       class change (the measure forces layout): a frame later, the first frame in the new
       box drew the OLD box's zoom and then snapped, which read as a jump. */
    function refit() {
      var f = viewers[front];
      if (!f || !f.loaded) return;
      var c = readCamera(f), d = defaultCamera(f);
      if (!d) return;
      if (c) { d.theta = c.theta; d.phi = c.phi; }
      restoreCamera(f, d);
    }

    /* Tucked once the add-on block's bottom edge has slid up under the card, so it is
       gone before the next section shows, and back the moment the block reappears. */
    function addonsUnderCard() {
      if (!addonsBox) return true;
      return addonsBox.getBoundingClientRect().bottom > topEdge() + layer.offsetHeight;
    }

    function sync() {
      if (!layer) return;
      var want = armed && !dismissed && inView && !galleryShown;
      if (want !== docked) {
        docked = want;
        if (want) sizeIt();
        layer.classList.add('is-resizing');
        requestAnimationFrame(function () { requestAnimationFrame(function () {
          layer.classList.remove('is-resizing');
        }); });
        layer.classList.toggle('is-docked', want);
        layer.classList.remove('is-settled');
        if (want) setTimeout(function () { if (docked) layer.classList.add('is-settled'); }, 400);
        refit();
      }
      layer.classList.toggle('is-tucked', docked && !addonsUnderCard());
      emit();
    }

    /* Tells the sticky Add to Cart (sections/product-main.liquid) when the card is
       floating, so only one floating thing is ever on screen. */
    var floating = false;
    function emit() {
      var f = !!layer && docked && layer.classList.contains('is-on') && !layer.classList.contains('is-tucked');
      if (f === floating) return;
      floating = f;
      document.dispatchEvent(new CustomEvent('ks:model-float', { detail: { floating: f } }));
    }

    /* The tapped card must not end up under the floating card: if it would, nudge the
       page just enough to clear it (the only time this ever scrolls the page). */
    function clearTapped(el) {
      var card = el && el.closest ? el.closest('[data-addon-item]') || el : null;
      if (!card) return;
      requestAnimationFrame(function () {
        var under = topEdge() + layer.offsetHeight + 12;
        var top = card.getBoundingClientRect().top;
        if (top < under) window.scrollBy({ top: top - under, behavior: 'smooth' });
      });
    }

    var io = new IntersectionObserver(function (es) {
      es.forEach(function (en) {
        if (en.target === main) galleryShown = en.intersectionRatio >= 0.35;
      });
      sync();
    }, { threshold: [0, 0.35] });
    io.observe(main);

    window.addEventListener('scroll', function () {
      if (!docked) return;
      layer.style.setProperty('--ks-dock-top', topEdge() + 'px');
      layer.classList.toggle('is-tucked', !addonsUnderCard());
      emit();
    }, { passive: true });

    return {
      attach: function () {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ks-addon-model__undock';
        b.setAttribute('aria-label', 'Close 3D preview');
        b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
        b.addEventListener('click', function (e) {
          e.stopPropagation();
          dismissed = true;
          sync();
        });
        layer.appendChild(b);
      },
      /* The x is final for this page visit (Keelan 10-27): later add-on taps never pop the
         card again. The gallery's 3D view keeps updating, so scrolling up shows it. */
      request: function (el) {
        if (dismissed) return;
        armed = true;
        var wasDocked = docked;
        /* layer is built on the first tap, after this runs: wait a frame for it. */
        var until = Date.now() + 8000;
        var go = function () {
          if (!layer) { if (Date.now() < until) requestAnimationFrame(go); return; }
          sync();
          if (docked && !wasDocked) clearTapped(el);
        };
        go();
      },
      release: function () { armed = false; sync(); emit(); },
      isDocked: function () { return docked; },
      emit: emit
    };
  })();
})();
